import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Container, IconButton, ListItemText, Menu, MenuItem, Stack,
  TextField, Tooltip, Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChatIcon from "@mui/icons-material/Chat";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { toast } from "react-toastify";
import backendApi from "../../../utils/BackendApi";
import ProductionChatLots from "./ProductionChatLots";

const CHAT_API = "/api/production-chat";
const CHAT_FILTERS = [["UNREAD", "Non letti"], ["MENTIONS", "Menzioni a me"]];
const ocLabel = order => `${order.tipoDoc || "OC"} ${order.numDoc}/${order.anno}`;

export default function ProductionChats() {
  const [access, setAccess] = useState(null);
  const [accessError, setAccessError] = useState("");
  const [orders, setOrders] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loadingLots, setLoadingLots] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState([]);
  const [workedByMe, setWorkedByMe] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [selected, setSelected] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [loadingChat, setLoadingChat] = useState(false);
  const [sending, setSending] = useState(false);
  const requestRef = useRef(0);
  const lastReadRef = useRef(new Map());
  const summaryRequestRef = useRef(0);
  const summaryInFlight = useRef(false);
  const messagesInFlight = useRef(new Set());
  const fileInputRef = useRef(null);
  const messagesBoxRef = useRef(null);
  const draftKeyRef = useRef("");

  const loadOrders = useCallback(async () => {
    setLoadingLots(true);
    setAccessError("");
    try {
      const result = await backendApi.get(`${CHAT_API}/admin/access`);
      setAccess(result.data);
      const response = await backendApi.get(`${CHAT_API}/admin/orders`);
      setOrders(response.data || []);
    } catch (error) {
      setAccessError(error.response?.status === 403 ? "Accesso riservato ai referenti d’ufficio abilitati."
        : "Impossibile caricare le chat. Verifica la connessione e riprova.");
    } finally { setLoadingLots(false); }
  }, []);
  useEffect(() => { loadOrders(); }, [loadOrders]);
  const lottiKey = JSON.stringify([...new Set(orders.map(order => order.lotto).filter(Boolean))].sort());
  const refreshSummaries = useCallback(async () => {
    if (summaryInFlight.current || document.hidden) return;
    const request = ++summaryRequestRef.current;
    const lotti = JSON.parse(lottiKey);
    if (!access || !lotti.length) return;
    summaryInFlight.current = true;
    try {
      const response = await backendApi.post(`${CHAT_API}/admin/conversations/summaries`, lotti);
      if (request === summaryRequestRef.current) setSummaries(response.data || []);
    } catch (error) { console.error("Errore riepilogo chat", error); }
    finally { summaryInFlight.current = false; }
  }, [access, lottiKey]);
  useEffect(() => {
    refreshSummaries();
    const interval = setInterval(refreshSummaries, 10000);
    return () => { ++summaryRequestRef.current; clearInterval(interval); };
  }, [refreshSummaries]);

  const cards = useMemo(() => {
    const byOc = new Map(summaries.filter(item => item.idDocumento).map(item => [String(item.idDocumento), item]));
    const unique = new Map();
    orders.filter(order => order.idDocumento > 0 && order.lotto).forEach(order => unique.set(String(order.idDocumento), {
      ...order, ...byOc.get(String(order.idDocumento)), title: ocLabel(order), key: `OC:${order.idDocumento}`
    }));
    const list = [...unique.values()];
    return list.filter(item => {
      const text = `${item.title} ${item.lotto} ${item.ragioneSocialeCliente || ""} ${item.rifOrdCli || ""}`.toLowerCase();
      return text.includes(search.toLowerCase())
        && (!workedByMe || (access?.username && item.quotationCreatedBy
          && item.quotationCreatedBy.toLowerCase() === access.username.toLowerCase())) && (!filters.length
        || (filters.includes("UNREAD") && item.unreadCount > 0)
        || (filters.includes("MENTIONS") && item.mentionCount > 0));
    }).sort((left, right) => (right.latestMessageAt ? new Date(right.latestMessageAt).getTime() : 0)
      - (left.latestMessageAt ? new Date(left.latestMessageAt).getTime() : 0) || right.numDoc - left.numDoc);
  }, [orders, summaries, search, filters, workedByMe, access]);

  const markRead = useCallback(async (id, items) => {
    const latest = items[items.length - 1];
    if (!latest || lastReadRef.current.get(id) === latest.id) return;
    try {
      await backendApi.post(`${CHAT_API}/admin/conversations/${id}/read`, { lastReadMessageId: latest.id });
      lastReadRef.current.set(id, latest.id);
      refreshSummaries();
    } catch (error) { console.error("Errore salvataggio lettura", error); }
  }, [refreshSummaries]);
  const closeChat = () => {
    ++requestRef.current;
    setSelected(null); setConversation(null); setMessages([]); setParticipants([]); setBody("");
  };
  const openCard = async card => {
    if (sending) return;
    const request = ++requestRef.current;
    setSelected(card); setConversation(null); setMessages([]); setParticipants([]); setLoadingChat(true);
    draftKeyRef.current = `productionChatDraft:${access.identity}:${card.key}`;
    setBody(localStorage.getItem(draftKeyRef.current) || "");
    try {
      const url = card.idDocumento ? `${CHAT_API}/admin/conversations/by-oc/${card.idDocumento}`
        : `${CHAT_API}/admin/conversations/by-lotto/${encodeURIComponent(card.lotto)}`;
      const response = await backendApi.get(url);
      if (request !== requestRef.current) return;
      const conv = response.data;
      const result = await backendApi.get(`${CHAT_API}/admin/conversations/${conv.id}/messages`, { params: { limit: 100 } });
      if (request !== requestRef.current) return;
      setConversation(conv); setParticipants(conv.participants || []); setMessages(result.data || []);
      void markRead(conv.id, result.data || []);
    } catch (error) { if (request === requestRef.current) toast.error("Impossibile aprire la chat"); }
    finally { if (request === requestRef.current) setLoadingChat(false); }
  };
  const refreshMessages = useCallback(async (showToast = false) => {
    if (!conversation?.id || messagesInFlight.current.has(conversation.id) || document.hidden) return;
    messagesInFlight.current.add(conversation.id);
    const request = requestRef.current;
    try {
      const response = await backendApi.get(`${CHAT_API}/admin/conversations/${conversation.id}/messages`, { params: { limit: 100 } });
      if (request !== requestRef.current) return;
      setMessages(response.data || []);
      await markRead(conversation.id, response.data || []);
      if (showToast) toast.success("Chat aggiornata");
    } catch (error) { if (showToast && request === requestRef.current) toast.error("Errore aggiornamento chat"); }
    finally { messagesInFlight.current.delete(conversation.id); }
  }, [conversation?.id, markRead]);
  useEffect(() => {
    if (!conversation?.id) return;
    const interval = setInterval(() => refreshMessages(), 5000);
    return () => clearInterval(interval);
  }, [conversation?.id, refreshMessages]);
  useEffect(() => {
    if (messagesBoxRef.current) messagesBoxRef.current.scrollTop = messagesBoxRef.current.scrollHeight;
  }, [messages.length, selected?.key]);
  const updateBody = value => {
    setBody(value);
    if (draftKeyRef.current) localStorage.setItem(draftKeyRef.current, value);
  };
  const sendMessage = async () => {
    if (!conversation?.id || !selected?.idDocumento || !body.trim() || sending) return;
    const request = requestRef.current;
    const text = encodeMentions(body.trim());
    setSending(true);
    try {
      const response = await backendApi.post(`${CHAT_API}/admin/conversations/${conversation.id}/messages`, {
        body: text, mentions: (text.match(/@([A-Za-z0-9._-]+)/g) || []).map(value => value.slice(1))
      });
      if (request !== requestRef.current) return;
      updateBody("");
      setMessages(previous => previous.some(item => item.id === response.data.id) ? previous : [...previous, response.data]);
      await refreshMessages();
    } catch (error) { if (request === requestRef.current) toast.error("Messaggio non inviato"); }
    finally { setSending(false); }
  };
  const uploadAttachment = async file => {
    if (!conversation?.id || !selected?.idDocumento || !file || sending) return;
    const request = requestRef.current;
    const data = new FormData(); data.append("file", file);
    if (body.trim()) data.append("body", encodeMentions(body.trim()));
    setSending(true);
    try {
      const response = await backendApi.post(`${CHAT_API}/admin/conversations/${conversation.id}/attachments`, data);
      if (request !== requestRef.current) return;
      updateBody("");
      setMessages(previous => previous.some(item => item.id === response.data.id) ? previous : [...previous, response.data]);
      await refreshMessages();
    } catch (error) { if (request === requestRef.current) toast.error("Allegato non inviato"); }
    finally { setSending(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };
  const mentionLabel = participant => participant.displayName || participant.matricola;
  const insertMention = participant => updateBody(`${body}${body && !body.endsWith(" ") ? " " : ""}@${mentionLabel(participant)} `);
  const encodeMentions = text => [...participants].sort((left, right) => mentionLabel(right).length - mentionLabel(left).length)
    .reduce((value, participant) => value.split(`@${mentionLabel(participant)}`).join(`@${participant.matricola}`), text);
  const displayBody = text => (text || "").replace(/@([A-Za-z0-9._-]+)/g, (token, code) => {
    const participant = participants.find(item => item.matricola === code);
    return participant ? `@${mentionLabel(participant)}` : token;
  });
  const downloadAttachment = async (attachment) => {
    if (!attachment?.id) return;

    try {
      const res = await backendApi.get(`${CHAT_API}/admin/attachments/${attachment.id}`, {
        responseType: "blob"
      });

      const url = URL.createObjectURL(new Blob([res.data], {
        type: res.headers["content-type"] || attachment.contentType || "application/octet-stream"
      }));
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.originalFileName || "allegato";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error(err);
      toast.error("Allegato non scaricabile");
    }
  };

  const renderMessage = (message) => {
    const own = message.senderMatricola === access?.identity;
    const attachment = message.attachments?.[0];

    return (
      <Box
        key={message.id}
        sx={{
          display: "flex",
          justifyContent: own ? "flex-end" : "flex-start",
          mb: 1.25
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: "92%", md: "74%" },
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: own ? "#1f6feb" : "#f1f5f9",
            color: own ? "#fff" : "#172033",
            border: own ? "none" : "1px solid #e5e7eb"
          }}
        >
          <Typography variant="caption" sx={{ display: "block", fontWeight: 700, opacity: 0.82 }}>
            {message.senderName || message.senderMatricola}
          </Typography>
          {message.body && (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {displayBody(message.body)}
            </Typography>
          )}
          {attachment && (
            <Chip
              icon={<AttachFileIcon />}
              label={attachment.originalFileName}
              size="small"
              onClick={() => downloadAttachment(attachment)}
              clickable
              sx={{
                mt: message.body ? 1 : 0.5,
                maxWidth: "100%",
                bgcolor: own ? "rgba(255,255,255,0.22)" : undefined,
                color: own ? "#fff" : undefined,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                },
                "& .MuiChip-icon": {
                  color: own ? "#fff" : undefined
                }
              }}
            />
          )}
          <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.68 }}>
            {message.createdAt
              ? new Date(message.createdAt).toLocaleString("it-IT", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit"
              })
              : ""}
          </Typography>
        </Box>
      </Box>
    );
  };

  return <Container maxWidth={false} sx={{ mt: 3, pb: 4 }}><Box sx={{ maxWidth: 1360 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
      <Box><Typography variant="h5" sx={{ fontWeight: 800 }}>Chat Produzione</Typography>
        <Typography variant="body2" color="text.secondary">Chiarimenti e allegati per singola OC.</Typography></Box>
      <Tooltip title="Aggiorna OC"><IconButton onClick={() => { loadOrders(); refreshSummaries(); }} disabled={loadingLots}><RefreshIcon /></IconButton></Tooltip>
    </Stack>
    {accessError ? <Alert severity="warning" action={<Button onClick={loadOrders}>Riprova</Button>}>{accessError}</Alert> : <>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button id="chat-filter-button" variant="outlined" endIcon={<KeyboardArrowDownIcon />}
          aria-haspopup="menu" aria-controls={filterAnchor ? "chat-filter-menu" : undefined}
          aria-expanded={Boolean(filterAnchor)} onClick={event => setFilterAnchor(event.currentTarget)}>
          Filtri: {[...CHAT_FILTERS.filter(([value]) => filters.includes(value)).map(([, label]) => label),
            ...(workedByMe ? ["Seguiti da me"] : [])].join(", ") || "Tutte"}
        </Button>
        <Menu id="chat-filter-menu" anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)}
          MenuListProps={{ "aria-labelledby": "chat-filter-button" }}>
          <MenuItem role="menuitemcheckbox" aria-checked={!filters.length && !workedByMe} onClick={() => { setFilters([]); setWorkedByMe(false); }}>
            <Checkbox checked={!filters.length && !workedByMe} tabIndex={-1} disableRipple inputProps={{ "aria-hidden": true }} />
            <ListItemText>Tutte</ListItemText>
          </MenuItem>
          {CHAT_FILTERS.map(([value, label]) => <MenuItem key={value} role="menuitemcheckbox"
            aria-checked={filters.includes(value)} onClick={() => setFilters(current => current.includes(value)
              ? current.filter(item => item !== value) : [...current, value])}>
            <Checkbox checked={filters.includes(value)} tabIndex={-1} disableRipple inputProps={{ "aria-hidden": true }} />
            <ListItemText>{label}</ListItemText>
          </MenuItem>)}
          <MenuItem role="menuitemcheckbox" aria-checked={workedByMe} onClick={() => setWorkedByMe(current => !current)}>
            <Checkbox checked={workedByMe} tabIndex={-1} disableRipple inputProps={{ "aria-hidden": true }} />
            <ListItemText>Seguiti da me</ListItemText>
          </MenuItem>
        </Menu>
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: selected ? "360px minmax(0,1fr)" : "1fr" }, gap: 2, alignItems: "start" }}>
        <Box sx={{ display: { xs: selected ? "none" : "block", lg: "block" } }}><TextField fullWidth size="small" label="Cerca OC, lotto, cliente o riferimento…" value={search} onChange={event => setSearch(event.target.value)} sx={{ mb: 2 }} />
          {loadingLots ? <CircularProgress /> : !cards.length ? <Alert severity="info">Nessuna conversazione corrisponde ai filtri.</Alert>
            : <ProductionChatLots cards={cards} selected={selected} sending={sending} onOpen={openCard}
                search={search} filterKey={JSON.stringify([filters, workedByMe])} filtered={Boolean(filters.length || workedByMe)} />}
        </Box>
        {selected && <Box sx={{ height: { xs: "75vh", lg: "calc(100vh - 220px)" }, minHeight: 500, display: "flex", flexDirection: "column", border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff", overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", p: 2, gap: 1, borderBottom: "1px solid #e5e7eb" }}>
            <IconButton sx={{ display: { xs: "inline-flex", lg: "none" } }} onClick={closeChat} disabled={sending} aria-label="Torna alla lista"><ArrowBackIcon /></IconButton>
            <ChatIcon color="primary" /><Box sx={{ flex: 1 }}><Typography fontWeight={800}>{selected.title}</Typography>
              <Typography variant="caption">Lotto {conversation?.lotto || selected.lotto}</Typography>
              <Typography variant="body2" color="text.secondary">Seguito da: {selected.quotationCreatedByName || selected.quotationCreatedBy || "Non registrato"}</Typography></Box>
            <IconButton onClick={() => refreshMessages(true)} disabled={!conversation || loadingChat} aria-label="Aggiorna chat"><RefreshIcon /></IconButton>
            <IconButton onClick={closeChat} disabled={sending} aria-label="Chiudi chat"><CloseIcon /></IconButton>
          </Box>
          {!selected.idDocumento ? <Alert severity="info">Storico condiviso del lotto. Le nuove richieste vanno scritte nelle chat OC.</Alert>
            : <Box sx={{ p: 1.5 }}>{["OFFICE", "WORKER"].map(kind => <Box key={kind} sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">{kind === "OFFICE" ? "Ufficio" : "Produzione"}</Typography>
              <Stack direction="row" spacing={1} sx={{ overflowX: "auto", py: 0.5 }}>{participants.filter(item => item.kind === kind).map(item =>
                <Chip key={item.matricola} label={item.displayName || item.matricola} size="small" onClick={() => insertMention(item)} disabled={sending} />)}</Stack>
            </Box>)}</Box>}
          <Box ref={messagesBoxRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2, bgcolor: "#fbfcfe" }}>
            {loadingChat ? <CircularProgress /> : messages.length ? messages.map(renderMessage) : <Typography color="text.secondary">Nessun messaggio.</Typography>}
          </Box>
          {!!selected.idDocumento && <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
            <input ref={fileInputRef} type="file" hidden onChange={event => uploadAttachment(event.target.files?.[0])} />
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
              <IconButton aria-label="Allega file" onClick={() => fileInputRef.current?.click()} disabled={!conversation || sending || loadingChat}><AttachFileIcon /></IconButton>
              <TextField fullWidth size="small" multiline maxRows={4} placeholder="Scrivi un chiarimento per questa OC…" value={body}
                onChange={event => updateBody(event.target.value)} disabled={!conversation || sending || loadingChat}
                onKeyDown={event => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendMessage(); } }} />
              <IconButton aria-label="Invia messaggio" color="primary" onClick={sendMessage} disabled={!conversation || !body.trim() || sending || loadingChat}><SendIcon /></IconButton>
            </Box>
            <Typography variant="caption" color="text.secondary">Tocca un nome per menzionarlo. La chat è condivisa con i partecipanti dell’OC.</Typography>
          </Box>}
        </Box>}
      </Box>
    </>}
  </Box></Container>;
}
